'use client'

import React, { useState, useEffect } from 'react'
import { useDict } from '@/lib'
import { classnames } from '@/utils/classnames'
import * as Icons from '../icon/icons'
import s from './SocialLinks.module.scss'

// Dostępne ikony
export type SocialName =
  | 'facebook'
  | 'instagram'
  | 'x'
  | 'linkedin'
  | 'tiktok'
  | 'messenger'
  | 'whatsapp'
  | 'github'
  | 'www'
  | 'threads'

// Typ dla social item - może być nazwa (string) lub obiekt z custom linkiem
export type SocialItem = SocialName | Record<string, string>

// Mapowanie nazw na komponenty ikon
const iconMap: Record<SocialName, keyof typeof Icons> = {
  facebook: 'FacebookIcon',
  instagram: 'InstagramIcon',
  x: 'XIcon',
  linkedin: 'LinkedInIcon',
  tiktok: 'TiktokIcon',
  messenger: 'MessengerIcon',
  whatsapp: 'WhatsappIcon',
  github: 'GithubIcon',
  www: 'WWWIcon',
  threads: 'ThreadsIcon',
}

/** Klucze z API / bazy (np. global-links, socials w profilu szkoły) bywają skrótami — mapujemy na SocialName */
const SHORT_KEY_TO_SOCIAL: Record<string, SocialName> = {
  fb: 'facebook',
  ig: 'instagram',
}

function normalizeSocialKey(raw: string): SocialName {
  const lower = raw.toLowerCase()
  return (SHORT_KEY_TO_SOCIAL[lower] ?? lower) as SocialName
}

interface SocialLinksProps {
  socials: SocialItem[]
  variant?: 'default' | 'custom'
  customLinks?: Partial<Record<SocialName, string>>
  iconColor?: string
  hoverIconColor?: string
  backgroundColor?: string
  hoverBackgroundColor?: string
  className?: string
  useGlobalSocials?: boolean // Nowy prop - czy używać globalnych social media
  globalSocials?: Record<string, string> | null // Opcjonalne globalne social media
  onLinkClick?: () => void
}

export function SocialLinks({
  socials,
  variant = 'default',
  customLinks = {},
  iconColor,
  hoverIconColor,
  backgroundColor,
  hoverBackgroundColor,
  className,
  useGlobalSocials = false,
  globalSocials: propGlobalSocials,
  onLinkClick,
}: SocialLinksProps) {
  const socialsDict = useDict('socials')
  const [globalSocials, setGlobalSocials] = useState<Record<
    string,
    string
  > | null>(null)

  // Załaduj globalne social media jeśli są potrzebne
  useEffect(() => {
    if (propGlobalSocials) {
      setGlobalSocials(propGlobalSocials)
      return
    }

    if (useGlobalSocials) {
      const loadGlobalSocials = async () => {
        try {
          const response = await fetch('/api/global-links')
          if (response.ok) {
            const data = await response.json()
            setGlobalSocials(data.socials)
          }
        } catch (error) {
          console.error('Error loading global social media:', error)
        }
      }
      loadGlobalSocials()
    }
  }, [useGlobalSocials, propGlobalSocials])

  const getLink = (socialName: SocialName, customUrl?: string): string => {
    // Custom URL z obiektu ma najwyższy priorytet
    if (customUrl) {
      return customUrl
    }

    // Custom links mają priorytet
    if (variant === 'custom' && customLinks[socialName]) {
      return customLinks[socialName]!
    }

    // Globalne social media z bazy danych (jeśli są dostępne)
    if (useGlobalSocials && globalSocials) {
      const globalKey =
        socialName === 'facebook'
          ? 'fb'
          : socialName === 'instagram'
            ? 'ig'
            : socialName
      if (globalSocials[globalKey] && globalSocials[globalKey].trim() !== '') {
        return globalSocials[globalKey]
      }
    }

    // Socials dict - z fallback
    if (socialsDict?.platforms) {
      const platform = socialsDict.platforms.find(
        (item: any) => item.name === socialName,
      )
      if (platform?.url) {
        return platform.url
      }
    }

    // Brak linku - zwróć pusty
    return '#'
  }

  const getPlatformData = (socialName: SocialName) => {
    if (socialsDict?.platforms) {
      return socialsDict.platforms.find((item: any) => item.name === socialName)
    }
    // Fallback data when dict doesn't work
    const fallbackData: Record<SocialName, any> = {
      facebook: {
        title: 'Odwiedź nas na Facebooku',
        ariaLabel: 'Facebook - Zebra.to',
      },
      instagram: {
        title: 'Śledź nas na Instagramie',
        ariaLabel: 'Instagram - Zebra.to',
      },
      x: { title: 'Śledź nas na X', ariaLabel: 'X - Zebra.to' },
      tiktok: {
        title: 'Zobacz nas na TikToku',
        ariaLabel: 'TikTok - Zebra.to',
      },
      linkedin: {
        title: 'Dołącz do nas na LinkedIn',
        ariaLabel: 'LinkedIn - Zebra.to',
      },
      messenger: {
        title: 'Napisz do nas na Messenger',
        ariaLabel: 'Messenger - Zebra.to',
      },
      whatsapp: {
        title: 'Napisz do nas na WhatsApp',
        ariaLabel: 'WhatsApp - Zebra.to',
      },
      github: { title: 'Zobacz nas na GitHub', ariaLabel: 'GitHub - Zebra.to' },
      www: {
        title: 'Odwiedź naszą stronę',
        ariaLabel: 'Strona WWW - Zebra.to',
      },
      threads: {
        title: 'Śledź nas na Threads',
        ariaLabel: 'Threads - Zebra.to',
      },
    }
    return fallbackData[socialName] || null
  }

  // Parsuj socials - każdy item może być string lub obiekt
  const parsedSocials = socials
    .map((item, index) => {
      if (typeof item === 'string') {
        const name = normalizeSocialKey(item)
        if (!(name in iconMap)) {
          return null
        }
        return {
          name,
          url: getLink(name),
          key: `${name}-${index}`,
        }
      } else {
        // Obiekt z custom linkami (klucz bywa np. fb / ig jak w CMS)
        const entries = Object.entries(item) as [string, string][]
        if (entries.length > 0) {
          const [rawKey, customUrl] = entries[0]
          const name = normalizeSocialKey(rawKey)
          if (name in iconMap) {
            return {
              name,
              url: getLink(name, customUrl || undefined),
              key: `${name}-${index}`,
            }
          }
        }
      }
      return null
    })
    .filter(Boolean)
    .filter((social) => social && social.url !== '#')

  return (
    <div className={classnames(s.container, className)}>
      {parsedSocials.map((social) =>
        social ? (
          <SocialIcon
            key={social.key}
            name={social.name}
            href={social.url}
            platformData={getPlatformData(social.name)}
            iconColor={iconColor}
            hoverIconColor={hoverIconColor}
            backgroundColor={backgroundColor}
            hoverBackgroundColor={hoverBackgroundColor}
            onLinkClick={onLinkClick}
          />
        ) : null,
      )}
    </div>
  )
}

// Pojedyncza ikona
interface SocialIconProps {
  name: SocialName
  href: string
  platformData?: any
  iconColor?: string
  hoverIconColor?: string
  backgroundColor?: string
  hoverBackgroundColor?: string
  onLinkClick?: () => void
}

function SocialIcon({
  name,
  href,
  platformData,
  iconColor,
  hoverIconColor,
  backgroundColor,
  hoverBackgroundColor,
  onLinkClick,
}: SocialIconProps) {
  const [isHovered, setIsHovered] = useState(false)

  const IconComponent = Icons[iconMap[name]] as React.ComponentType<
    React.SVGProps<SVGSVGElement>
  >

  if (!IconComponent) {
    console.warn(
      'SocialIcon: No IconComponent found for name:',
      name,
      'Available icons:',
      Object.keys(Icons),
    )
    return null
  }

  // Tylko custom style jeśli są podane propsy
  const customStyle: React.CSSProperties = {}
  if (backgroundColor || hoverBackgroundColor) {
    customStyle.backgroundColor = isHovered
      ? hoverBackgroundColor || backgroundColor
      : backgroundColor
  }
  if (iconColor || hoverIconColor) {
    customStyle.color = isHovered ? hoverIconColor || iconColor : iconColor
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={s.socialIcon}
      style={customStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onLinkClick}
      title={platformData?.title || `Odwiedź nas na ${name}`}
      aria-label={platformData?.ariaLabel || `${name} - Zebra.to`}
    >
      <IconComponent width={16} height={16} />
    </a>
  )
}
